import * as React from 'react';
import { createContext, useContext } from 'react';
import {
  MerchantApplicationContextModel,
  PatchType,
  StepDataModel,
  StepKeyType,
  AISuggestionMap,
} from 'types/interfaces/sign-up/MerchantApplication.types';

import { FINAL_STAGES, useActivationFunctions } from 'utils';

import {
  getSkippedSteps,
  signUpStepsConfig,
  SignUpStepKey,
} from 'modules/sign-up/signUpConfig';
import {
  ActivationStepKey,
  getActivationStepIndex,
} from 'modules/activation/ActivationConfig';
import {
  ACTIVATION_DATA_LOCAL_STORAGE_KEY,
  getStoredActivationData,
} from 'modules/activation/activationStorage';
import { type ActivationStepDataModel } from 'modules/activation/steps/activationStepData';
import { ApplicationFormOptionsMap } from 'types/responses/sign-up/ApplicationFormOptionsResponse';
import {
  MerchantApplicationResponse,
  MerchantApplicationStatus,
} from 'types/responses/activation';

export { ACTIVATION_DATA_LOCAL_STORAGE_KEY, FINAL_STAGES };

export type StepState = {
  activeIndex: number;
  activeStepKey: SignUpStepKey;
  stepData: StepDataModel;
};

export type ActivationStepDataState = {
  patchType?: PatchType;
  merchantApplicationDetails: Partial<ActivationStepDataModel>;
  committedStepDetails: Partial<ActivationStepDataModel>;
};

export type ActivationStepState = {
  activeIndex: number;
  activeStepKey: ActivationStepKey;
  stepData: ActivationStepDataState;
};

export interface MerchantApplicationContextInterface {
  stepState: StepState;

  activationData: MerchantApplicationResponse | null;
  activationDataLoading: boolean;
  applicationStatus: MerchantApplicationStatus | null;
  applicantFirstName?: string | null;
  applicationReferrer?: string;
  setApplicationReferrer?: (applicationReferrer?: string) => void;
  isPostSubmission: () => boolean;
  salesforceSdocContractUrl?: string | null;
  getActivationData: () => Promise<MerchantApplicationResponse | null>;
  refreshActivationData: () => Promise<MerchantApplicationResponse | null>;

  setStepData: <K extends StepKeyType>(
    step: K,
    data: Partial<MerchantApplicationContextModel[K]>,
    patchType?: PatchType,
    merge?: boolean
  ) => void;

  commitStepData: <K extends StepKeyType>(step: K) => void;

  getStepData: <K extends StepKeyType>(
    step: K
  ) => Partial<MerchantApplicationContextModel[K]>;

  getCommittedStepData: <K extends StepKeyType>(
    step: K
  ) => Partial<MerchantApplicationContextModel[K]>;

  getStepField: <
    K extends StepKeyType,
    F extends keyof MerchantApplicationContextModel[K],
  >(
    step: K,
    field: F
  ) => MerchantApplicationContextModel[K][F] | undefined;

  nextSignUpStep: () => void;
  prevSignUpStep: () => void;

  activationStepState: ActivationStepState;
  setActivationActiveStep: (step: ActivationStepKey) => void;
  setActivationStepData: <K extends ActivationStepKey>(
    step: K,
    data: Partial<ActivationStepDataModel[K]>,
    merge?: boolean
  ) => void;
  commitActivationStepData: <K extends ActivationStepKey>(step: K) => void;
  getActivationStepData: <K extends ActivationStepKey>(
    step: K
  ) => Partial<ActivationStepDataModel[K]>;

  merchantApplicationId?: string;
  setMerchantApplicationId: (id?: string) => void;

  utmCampaign?: string;
  setUtmCampaign?: (utmCampaign?: string) => void;

  aiSuggestions: AISuggestionMap;
  setAISuggestionsInContext: <K extends keyof AISuggestionMap>(
    key: K,
    suggestion: AISuggestionMap[K]
  ) => void;

  formOptions: ApplicationFormOptionsMap;
  formOptionsLoading: boolean;
  setFormOptions: (options: ApplicationFormOptionsMap) => void;
  setFormOptionsLoading: (loading: boolean) => void;
}

const MerchantApplicationContext =
  createContext<MerchantApplicationContextInterface | null>(null);

const isMerchantApplicationStatus = (
  status: unknown
): status is MerchantApplicationStatus =>
  typeof status === 'string' &&
  Object.values(MerchantApplicationStatus).includes(
    status as MerchantApplicationStatus
  );

export const useMerchantApplicationContext =
  (): MerchantApplicationContextInterface => {
    const ctx = useContext(MerchantApplicationContext);
    if (!ctx) {
      throw new Error(
        'useMerchantApplicationContext must be used within MerchantApplicationProvider'
      );
    }
    return ctx;
  };

export function MerchantApplicationProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const [merchantApplicationId, setMerchantApplicationId] = React.useState<
    string | undefined
  >();
  const [utmCampaign, setUtmCampaign] = React.useState<string | undefined>();
  const cachedActivationData = React.useMemo(getStoredActivationData, []);
  const [applicationStatus, setApplicationStatus] =
    React.useState<MerchantApplicationStatus | null>(
      isMerchantApplicationStatus(cachedActivationData?.status)
        ? cachedActivationData.status
        : null
    );
  const [applicantFirstName, setApplicantFirstName] = React.useState<
    string | null
  >(cachedActivationData?.signUpInfo?.firstName ?? null);
  const [applicationReferrer, setApplicationReferrer] = React.useState<
    string | undefined
  >(cachedActivationData?.signUpInfo?.referrer?.trim() || undefined);
  const [activationData, setActivationData] =
    React.useState<MerchantApplicationResponse | null>(cachedActivationData);
  const [activationDataLoading, setActivationDataLoading] =
    React.useState(!cachedActivationData);
  const activationDataRequestRef =
    React.useRef<Promise<MerchantApplicationResponse | null> | null>(null);
  const [salesforceSdocContractUrl, setSalesforceSdocContractUrl] =
    React.useState<string | null>();
  const { getActivationData } = useActivationFunctions();

  const isPostSubmission = React.useCallback((): boolean => {
    return (
      applicationStatus != null && FINAL_STAGES.includes(applicationStatus)
    );
  }, [applicationStatus]);

  const [aiSuggestions, setAISuggestions] = React.useState<AISuggestionMap>({});

  const [formOptions, setFormOptions] =
    React.useState<ApplicationFormOptionsMap>({});
  const [formOptionsLoading, setFormOptionsLoading] = React.useState(true);

  const [stepState, setStepState] = React.useState<StepState>({
    activeIndex: 0,
    activeStepKey: SignUpStepKey.SignUpInfo,
    stepData: {
      merchantApplicationDetails: {},
      committedStepDetails: {},
      patchType: PatchType.SignUpInfo,
    },
  });

  const [activationStepState, setActivationStepState] =
    React.useState<ActivationStepState>({
      activeIndex: 0,
      activeStepKey: ActivationStepKey.LocationDetails,
      stepData: {
        merchantApplicationDetails: {},
        committedStepDetails: {},
        patchType: PatchType.ActivationInfo,
      },
    });

  const setStepData = React.useCallback(
    <K extends StepKeyType>(
      step: K,
      data: Partial<MerchantApplicationContextModel[K]>,
      patchType?: PatchType,
      merge = false
    ) => {
      setStepState((s) => {
        const existingForStep = (s.stepData.merchantApplicationDetails[step] ??
          {}) as Partial<MerchantApplicationContextModel[K]>;

        const nextForStep = merge
          ? {
              ...existingForStep,
              ...data,
            }
          : {
              ...data,
            };

        return {
          ...s,
          stepData: {
            ...s.stepData,
            ...(patchType !== undefined ? { patchType } : {}),

            merchantApplicationDetails: {
              ...s.stepData.merchantApplicationDetails,
              [step]: nextForStep,
            },
          },
        };
      });
    },
    []
  );

  const getStepData = React.useCallback(
    <K extends StepKeyType>(
      step: K
    ): Partial<MerchantApplicationContextModel[K]> => {
      return (stepState.stepData.merchantApplicationDetails[step] ??
        {}) as Partial<MerchantApplicationContextModel[K]>;
    },
    [stepState.stepData.merchantApplicationDetails]
  );

  const getStepField = <
    K extends StepKeyType,
    F extends keyof MerchantApplicationContextModel[K],
  >(
    step: K,
    field: F
  ): MerchantApplicationContextModel[K][F] | undefined => {
    return getStepData(step)[field];
  };

  /*
   * Cached activation data can seed the first render, but login still needs to
   * refresh from /MerchantApplications. This ref dedupes that request for the
   * current provider mount without suppressing it because localStorage exists.
   */
  const handleGetActivationData = React.useCallback(async () => {
    if (activationDataRequestRef.current) {
      return activationDataRequestRef.current;
    }

    setActivationDataLoading(true);

    const request = getActivationData()
      .then((data) => {
        const nextApplicationStatus = isMerchantApplicationStatus(data?.status)
          ? data.status
          : null;

        setActivationData(data);
        setApplicationStatus(nextApplicationStatus);
        setApplicantFirstName(data?.signUpInfo?.firstName ?? null);
        setApplicationReferrer(data?.signUpInfo?.referrer?.trim() || undefined);
        setSalesforceSdocContractUrl(
          data?.salesforceSdocContractUrl || undefined
        );
        return data;
      })
      .catch((error) => {
        activationDataRequestRef.current = null;
        throw error;
      })
      .finally(() => {
        setActivationDataLoading(false);
      });

    activationDataRequestRef.current = request;

    return request;
  }, [getActivationData]);

  const refreshActivationData = React.useCallback(async () => {
    activationDataRequestRef.current = null;
    return handleGetActivationData();
  }, [handleGetActivationData]);

  const nextSignUpStep = React.useCallback(() => {
    setStepState((s) => {
      const paymentType =
        s.stepData.merchantApplicationDetails.paymentUseCase?.paymentType;
      const skipped = getSkippedSteps(paymentType);
      const lastIndex = signUpStepsConfig.length - 1;
      if (s.activeIndex >= lastIndex) {
        return s;
      }
      let next = s.activeIndex + 1;
      while (
        next < lastIndex &&
        skipped.includes(signUpStepsConfig[next].key)
      ) {
        next++;
      }
      return {
        ...s,
        activeIndex: next,
        activeStepKey: signUpStepsConfig[next].key,
      };
    });
  }, []);

  const prevSignUpStep = React.useCallback(() => {
    setStepState((s) => {
      if (s.activeIndex <= 0) {
        return s;
      }
      const paymentType =
        s.stepData.merchantApplicationDetails.paymentUseCase?.paymentType;
      const skipped = getSkippedSteps(paymentType);
      const backwardSkipped =
        s.activeStepKey === SignUpStepKey.UserAccountCreation
          ? [...skipped, SignUpStepKey.EligibilityCheck]
          : skipped;
      let prev = s.activeIndex - 1;
      while (
        prev > 0 &&
        backwardSkipped.includes(signUpStepsConfig[prev].key)
      ) {
        prev--;
      }
      return {
        ...s,
        activeIndex: prev,
        activeStepKey: signUpStepsConfig[prev].key,
      };
    });
  }, []);

  const commitStepData = React.useCallback(<K extends StepKeyType>(step: K) => {
    setStepState((s) => {
      const draft = s.stepData.merchantApplicationDetails[step] ?? {};
      return {
        ...s,
        stepData: {
          ...s.stepData,
          committedStepDetails: {
            ...s.stepData.committedStepDetails,
            [step]: { ...draft },
          },
        },
      };
    });
  }, []);

  const getCommittedStepData = React.useCallback(
    <K extends StepKeyType>(
      step: K
    ): Partial<MerchantApplicationContextModel[K]> => {
      return (stepState.stepData.committedStepDetails?.[step] ?? {}) as Partial<
        MerchantApplicationContextModel[K]
      >;
    },
    [stepState.stepData.committedStepDetails]
  );

  const setActivationActiveStep = React.useCallback(
    (step: ActivationStepKey) => {
      setActivationStepState((s) => ({
        ...s,
        activeIndex: getActivationStepIndex(step),
        activeStepKey: step,
      }));
    },
    []
  );

  const setActivationStepData = React.useCallback(
    <K extends ActivationStepKey>(
      step: K,
      data: Partial<ActivationStepDataModel[K]>,
      merge = false
    ) => {
      setActivationStepState((s) => {
        const existingForStep = (s.stepData.merchantApplicationDetails[step] ??
          {}) as Partial<ActivationStepDataModel[K]>;

        const nextForStep = merge
          ? {
              ...existingForStep,
              ...data,
            }
          : {
              ...data,
            };

        return {
          ...s,
          stepData: {
            ...s.stepData,
            patchType: PatchType.ActivationInfo,
            merchantApplicationDetails: {
              ...s.stepData.merchantApplicationDetails,
              [step]: nextForStep,
            },
          },
        };
      });
    },
    []
  );

  const getActivationStepData = React.useCallback(
    <K extends ActivationStepKey>(
      step: K
    ): Partial<ActivationStepDataModel[K]> => {
      return (activationStepState.stepData.merchantApplicationDetails[step] ??
        {}) as Partial<ActivationStepDataModel[K]>;
    },
    [activationStepState.stepData.merchantApplicationDetails]
  );

  const commitActivationStepData = React.useCallback(
    <K extends ActivationStepKey>(step: K) => {
      setActivationStepState((s) => {
        const draft = s.stepData.merchantApplicationDetails[step] ?? {};
        return {
          ...s,
          stepData: {
            ...s.stepData,
            committedStepDetails: {
              ...s.stepData.committedStepDetails,
              [step]: { ...draft },
            },
          },
        };
      });
    },
    []
  );

  const setAISuggestionsInContext = React.useCallback(
    <K extends keyof AISuggestionMap>(
      key: K,
      suggestion: AISuggestionMap[K]
    ) => {
      setAISuggestions((prev) => ({ ...prev, [key]: suggestion }));
    },
    []
  );

  return (
    <MerchantApplicationContext.Provider
      value={{
        stepState,
        activationData,
        activationDataLoading,
        applicationStatus,
        applicantFirstName,
        applicationReferrer,
        setApplicationReferrer,
        salesforceSdocContractUrl,
        isPostSubmission,
        getActivationData: handleGetActivationData,
        refreshActivationData,
        setStepData,
        commitStepData,
        getStepData,
        getCommittedStepData,
        getStepField,
        nextSignUpStep,
        prevSignUpStep,
        activationStepState,
        setActivationActiveStep,
        setActivationStepData,
        commitActivationStepData,
        getActivationStepData,
        merchantApplicationId,
        setMerchantApplicationId,
        utmCampaign,
        setUtmCampaign,
        aiSuggestions,
        setAISuggestionsInContext,
        formOptions,
        formOptionsLoading,
        setFormOptions,
        setFormOptionsLoading,
      }}
    >
      {children}
    </MerchantApplicationContext.Provider>
  );
}
